import * as React from "react";
import {useContext, useEffect, useRef, useState} from "react";
import {
    BacktestResultsPageProps,
    StreamingResponseErrorDetail,
    TradingAlgorithmBacktestRequest, TradingBacktestResult,
    TradingAlgorithmBacktestStreamingResponse,
    TradingAlgoStored, StorableBacktestResultRequest
} from "../utils/typesTrading";
import AppTheme from "../themes/AppTheme";
import CssBaseline from "@mui/material/CssBaseline";
import Container from "@mui/material/Container";
import AppAppBar from "../components/AppAppBar";
import Footer from "../components/Footer";
import {AuthContext} from "../components/AuthContext";
import {useLocation, useNavigate} from "react-router";
import {
    backtestTradingAlgorithm,
    isMessagePositionIndicator,
    isStreamingResponseErrorDetail,
    isBacktestResultDetail,
    saveBacktestResult
} from "../apis/junkyard";
import ShareableBacktestLink from "../components/ShareableBacktestLink";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import {formatDate, formatDateForAPI} from "../utils/datetime";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {IconButton, LinearProgress, Tooltip} from "@mui/material";
import Alert from "@mui/material/Alert";
import {BacktestResultsGraph} from "../components/BacktestResultsGraph";
import {Save, IosShare} from "@mui/icons-material";
import {useSnackbar} from "../components/SnackbarContext";
import Stack from "@mui/material/Stack";
import {backtestTradingAlgorithmWs, makeBacktestCancelHandle} from "../apis/junkyardWs";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import {BacktestCancelHandle} from "../utils/typesTradingWs";

export const BacktestResultsPage: React.FC<BacktestResultsPageProps> = (props) => {
    const authContext = useContext(AuthContext);
    const {showSnackbar} = useSnackbar();
    const navigate = useNavigate()
    const location = useLocation();
    let [backtestDates, setBacktestDates] = React.useState<number[]>([]);
    let [strategyPctReturns, setStrategyPctReturns] = React.useState<number[]>([]);
    let [benchmarkPctReturns, setBenchmarkPctReturns] = React.useState<number[]>([]);
    let [strategyCashValues, setStrategyCashValues] = React.useState<number[]>([]);
    let [backtestResult, setBacktestResult] = React.useState<TradingBacktestResult | null>(null);
    let [backtestUniqueId, setBacktestUniqueId] = React.useState<string | null>(null);
    const [selectedTradingAlgo, setSelectedTradingAlgo] = React.useState<TradingAlgoStored | null>(location.state?.trading_algo as TradingAlgoStored || null);
    const [backtestStartDate, setBacktestStartDate] = React.useState<Date>(new Date(location.state?.backtestStartDate) ?? new Date('2024-01-01'))
    const [backtestEndDate, setBacktestEndDate] = React.useState<Date>(new Date(location.state?.backtestEndDate) ?? new Date(Date.now()))
    const [startingCash, setStartingCash] = React.useState<number>(parseFloat(location.state?.startingCash) ?? 10000.0)
    const [tickersUniverse, setTickersUniverse] = React.useState<string>(location.state?.tickersUniverse ?? "sp501")
    const [isLoading, setIsLoading] = React.useState<boolean>(true);
    const [isLoadingCircularProgress, setIsLoadingCircularProgress] = React.useState<boolean>(true);
    const [backtestProgress, setBacktestProgress] = React.useState<number>(0);
    const [currentLoadDate, setCurrentLoadDate] = React.useState<string>(formatDateForAPI(backtestStartDate));
    const [errorDetail, setErrorDetail] = React.useState<StreamingResponseErrorDetail | null>(null);
    const [isDirty, setIsDirty] = useState(true);
    const [isShareDialogOpen, setIsShareDialogOpen] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
    const cancelHandleRef = useRef<BacktestCancelHandle>(makeBacktestCancelHandle());

    const handleGoBack = () => {
        navigate("/")
    }

    const handleCancel = async () => {
        cancelHandleRef.current.close();
        showSnackbar("Backtest cancelled", "info");
        handleGoBack();
    }

    const getProgressPercentage =
        (snapshotDate: Date, startDate: Date, endDate: Date): number =>
    {
        const startTime = startDate.getTime();
        const endTime = endDate.getTime();
        const currentTime = snapshotDate.getTime();
        const result = Math.min(Math.max(((currentTime - startTime) / (endTime - startTime))*100.0, 0.0), 100.0);
        return Math.round(result*100) / 100;
    }

    const handleRunRequest = async () => {
        if (!selectedTradingAlgo) {
            console.warn("No trading algo detected.")
            return;
        }
        setIsDirty(false);
        console.info("Trading algo detected", selectedTradingAlgo)
        setBacktestProgress(0);
        setIsLoadingCircularProgress(true);
        setCurrentLoadDate(formatDateForAPI(backtestStartDate));
        let _backtestDates: number[] = [];
        let _strategyRawReturns: number[] = [];
        let _benchmarkRawReturns: number[] = [];
        let _strategyPctReturns: number[] = [];
        let _benchmarkPctReturns: number[] = [];
        let _backtestResult: TradingBacktestResult | null = null;

        try {
            const request = {
                'access_token': authContext.readAccessToken(),
                'trading_algo': selectedTradingAlgo,
                'start_date': formatDateForAPI(backtestStartDate),
                'end_date': formatDateForAPI(backtestEndDate),
                'tickers_universe': tickersUniverse,
                'starting_cash': startingCash,
            } as TradingAlgorithmBacktestRequest;
            console.info("Request", request);
            for await (const response of backtestTradingAlgorithmWs(request, cancelHandleRef.current)) {
                if (isStreamingResponseErrorDetail(response)) {
                    // show error
                    const eDetail = response as StreamingResponseErrorDetail;
                    setErrorDetail(eDetail);
                    break;
                } else if (isMessagePositionIndicator(response)) {
                    console.debug("Message response position: ", response.message_position);
                } else if (isBacktestResultDetail(response)) {
                    _backtestResult = response as TradingBacktestResult;
                } else {
                    setIsLoadingCircularProgress(false);
                    const dayResult = response as TradingAlgorithmBacktestStreamingResponse;
                    const snapshotDate = new Date(dayResult.snapshot_date);
                    _backtestDates.push(snapshotDate.getTime());

                    _strategyRawReturns.push(dayResult.portfolio.portfolio_value)
                    _benchmarkRawReturns.push(dayResult.benchmark.portfolio_value)

                    setCurrentLoadDate(formatDateForAPI(snapshotDate));
                    const currentProgress = getProgressPercentage(
                        snapshotDate,
                        backtestStartDate,
                        backtestEndDate
                    );
                    setBacktestProgress(currentProgress);
                    setBacktestDates(_backtestDates);
                    setStrategyPctReturns(_strategyPctReturns);
                    setBenchmarkPctReturns(_benchmarkPctReturns);
                    const initialPortfolioValue = _strategyRawReturns.at(0);
                    const initialBenchmarkPortfolioValue = _benchmarkRawReturns.at(0);

                    if (initialPortfolioValue === undefined) {
                        console.error("Undefined initial portfolio value.")
                        break;
                    }

                    if (initialBenchmarkPortfolioValue === undefined) {
                        console.error("Undefined initial benchmark portfolio value.")
                        break;
                    }

                    if (initialPortfolioValue === 0) {
                        _strategyPctReturns.push(0);
                    } else {
                        _strategyPctReturns.push(100*(dayResult.portfolio.portfolio_value - initialPortfolioValue) / initialPortfolioValue);
                    }

                    if (initialBenchmarkPortfolioValue === 0) {
                        _benchmarkPctReturns.push(0);
                    } else {
                        _benchmarkPctReturns.push(100*(dayResult.benchmark.portfolio_value - initialBenchmarkPortfolioValue) / initialBenchmarkPortfolioValue);
                    }
                    // re-enable to gradually show backtest results
                    // setIsLoading(false);
                    setIsLoadingCircularProgress(true);
                }

            }
        } catch (error) {
            console.error(error);
            showSnackbar("An error occurred while running the backtest.", "error");
            handleGoBack();
        }
        setBacktestDates(_backtestDates);
        setStrategyPctReturns(_strategyPctReturns);
        setBenchmarkPctReturns(_benchmarkPctReturns);
        setStrategyCashValues(_strategyRawReturns);
        console.info("Backtest Result", _backtestResult)
        setBacktestResult(_backtestResult)
        setIsLoading(false);
        setIsDirty(true);
    }

    useEffect(() => {
        const doHandleRunRequest = async () => {
            await handleRunRequest();
        }
        doHandleRunRequest();
    }, [])

    const renderError = () => {

        return (
            <Box sx={{ mt: 12, display: 'flex', flexDirection: 'column', alignItems: 'flex'}}>
                <Tooltip title="Go Back">
                    <IconButton onClick={handleGoBack} size="small">
                        <ArrowBackIcon/>
                    </IconButton>
                </Tooltip>
                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                    <Alert variant="filled" severity="error" title={"Error"} sx={{whiteSpace: 'pre-wrap', wordBreak: 'break-word'}}>
                        {errorDetail?.exception || "Unknown Error"}
                        <br />
                        {errorDetail?.traceback || "Unknown Error"}
                    </Alert>

                </Box>
            </Box>
        )

    }

    const handleShareBacktestResult = () => {
        setIsShareDialogOpen(true);
    }

    const handleCloseShareDialog = () => {
        setIsShareDialogOpen(false);
    }

    const handleSaveBacktestResult = async () => {
        setIsDirty(false);
        if (!selectedTradingAlgo) {
            return;
        }
        try {
            const req = {
                "access_token": authContext.readAccessToken(),
                "account_username": authContext.getCurrentUser(),
                "selected_trading_algo_id": selectedTradingAlgo.id,
                "backtest_start_date": backtestStartDate,
                "backtest_end_date": backtestEndDate,
                "backtest_dates": backtestDates,
                "strat_pct_returns": strategyPctReturns,
                "bmark_pct_returns": benchmarkPctReturns,
                "strat_cash_values": strategyCashValues,
                "backtest_result": backtestResult,
            } as StorableBacktestResultRequest;
            const resp = await saveBacktestResult(req);
            if (resp.success) {
               setIsDirty(false);
                showSnackbar("Backtest saved successfully", "info");
                if (resp.unique_id) {
                    setBacktestUniqueId(resp.unique_id)
                }
            } else {
                setIsDirty(true);
                showSnackbar("An error occurred while saving the result. Subscription tier must be plus or premium.", "error");
                setBacktestUniqueId(null);
            }
        } catch (error) {
            console.error("Failed to save backtest result", error)
            showSnackbar("An error occurred while saving the result", "error");
            setIsDirty(true);
            setBacktestUniqueId(null);
        }

    }


    return (
        <React.Fragment>
            <AppTheme>
                <CssBaseline enableColorScheme />
                <Box sx={{
                    mt: 5,
                    display: 'flex',
                    justifyContent: 'center',
                    width: "99%",
                    height: "80%",
                    minWidth: 1000,
                }}>

                    <AppAppBar></AppAppBar>
                    {authContext.isAuthenticated() && isLoading ? (
                        <Box sx={{justifyContent: 'center', display: 'flex', mt: 16, flexDirection: 'column', alignItems: 'center', width: '60%'}}>
                            <Typography variant="h5" sx={{mb: 4}}>
                                Backtesting...
                            </Typography>
                            <Typography variant="body1" sx={{mb: 2}}>
                                Processing {currentLoadDate} of {formatDateForAPI(backtestEndDate)}
                            </Typography>
                            {isLoadingCircularProgress &&
                                <Box sx={{m: 5}}>
                                    <CircularProgress/>
                                </Box>
                            }
                            <LinearProgress variant="determinate" value={backtestProgress} sx={{width: '100%'}} />
                            <Typography variant="body1" sx={{mb: 2, mt: 2}}>
                                {backtestProgress}%
                            </Typography>
                            <Button onClick={handleCancel} variant={"outlined"} color={"error"}>
                                Cancel
                            </Button>

                        </Box>
                    ) : (
                        (authContext.isAuthenticated()) ? (
                            (errorDetail !== null) ? (
                                renderError()
                            ) : (
                            <Box sx={{marginTop: 10}}>
                                <Box sx={{justifyContent: 'center', display: 'flex'}}>
                                    <Typography variant="h3">
                                        Backtest Results
                                    </Typography>
                                </Box>
                                <Box sx={{
                                    justifyContent: 'space-between',
                                    display: 'flex',
                                    width: '100%',
                                    borderBottom: 1,
                                    borderColor: 'divider',
                                    pb: 1,
                                    mb: 2,
                                }}>
                                    <Tooltip title="Go Back">
                                        <IconButton onClick={handleGoBack} size="small">
                                            <ArrowBackIcon/>
                                        </IconButton>
                                    </Tooltip>
                                    <Stack direction={"row"} spacing={1}>
                                    <Tooltip title={backtestUniqueId ? "Share" : "Save the backtest first to share it"}>
                                        <IconButton
                                            onClick={handleShareBacktestResult}
                                            size="small"
                                            disabled={!backtestUniqueId}
                                        >
                                            <IosShare />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Save">
                                        <IconButton
                                            onClick={handleSaveBacktestResult}
                                            size="small"
                                            disabled={!isDirty}
                                        >
                                            <Save />
                                        </IconButton>
                                    </Tooltip>
                                    </Stack>
                                </Box>
                                <Box sx={{flexDirection: 'row', height: "90%"}}>
                                    <BacktestResultsGraph
                                        selectedTradingAlgo={selectedTradingAlgo}
                                        backtestStartDate={backtestStartDate}
                                        backtestEndDate={backtestEndDate}
                                        backtestDates={backtestDates}
                                        stratPctReturns={strategyPctReturns}
                                        bmarkPctReturns={benchmarkPctReturns}
                                        stratCashValues={strategyCashValues}
                                        backtestResult={backtestResult}
                                        onGoBack={handleGoBack}
                                    />
                                </Box>
                            </Box>
                            )
                        ) : (
                            <Box sx={{justifyContent: 'center', padding: 20}}>
                                <Alert severity="error">
                                    You must be logged in to run a backtest
                                </Alert>
                            </Box>
                    ))}
                </Box>
                <Footer />
                <ShareableBacktestLink
                    open={isShareDialogOpen}
                    onClose={handleCloseShareDialog}
                    backtestUniqueId={backtestUniqueId}
                />
            </AppTheme>
        </React.Fragment>
    )

}
